"use client"

import { v7 as uuidv7 } from "uuid"
import { mirrorToLuigi } from "./scripts/luigisCollector"

export const dispatchGtag = (name: string, data: object) => {
  try {
    if (typeof window === "undefined") return
    window.gtag?.("event", name, data)
  } catch {
    // silent fail: tracking must never break the app
  }
}

// ── Bloomreach/Exponea pre-init queue ────────────────────────────────────────
//
// The vendor snippet stubs `window.exponea` with a queueing shim, so anything
// called after it executes but before `exponea.start()` is safely buffered by
// the SDK. Calls made BEFORE the snippet executes have no such luck: the
// optional call is a silent no-op and the payload is gone.
//
// That window is wide, not an edge case. `BloomreachLoad` is rendered by
// `StoreTrackingScripts`, which `TrackingLoader` only mounts once the beacon
// data fetch has resolved (`bootloader/TrackingLoader.tsx`: `if (!hydrated)
// return null`), and the snippet itself is `afterInteractive`. A/B exposure
// beacons, by contrast, fire from effects on the first client render. Without a
// queue their `split_test` events are dropped, and because exposure is deduped
// per `flag:variant` nothing ever retries, so the visitor's arm would never reach
// Bloomreach at all.
//
// So we hold calls until the stub appears and then flush in order.
const pendingExponeaCalls: Array<() => void> = []
let exponeaPollTimer: ReturnType<typeof setInterval> | null = null
let exponeaWaitedMs = 0

const EXPONEA_POLL_INTERVAL_MS = 200
// Matches the window `StoreTrackingScripts` waits before logging
// `[TRACKING_SDK_MISSING]`. Past it the SDK is blocked (ad-blocker, ~a third of
// traffic) and holding the queue would only leak memory.
const EXPONEA_READY_TIMEOUT_MS = 10_000

const isExponeaReady = () =>
  typeof window !== "undefined" && typeof window.exponea?.track === "function"

const stopExponeaPolling = () => {
  if (exponeaPollTimer) {
    clearInterval(exponeaPollTimer)
    exponeaPollTimer = null
  }
}

const flushPendingExponeaCalls = () => {
  while (pendingExponeaCalls.length > 0) {
    pendingExponeaCalls.shift()?.()
  }
}

const startExponeaPolling = () => {
  if (exponeaPollTimer || typeof window === "undefined") return
  exponeaWaitedMs = 0
  exponeaPollTimer = setInterval(() => {
    exponeaWaitedMs += EXPONEA_POLL_INTERVAL_MS
    if (isExponeaReady()) {
      stopExponeaPolling()
      flushPendingExponeaCalls()
      return
    }
    if (exponeaWaitedMs >= EXPONEA_READY_TIMEOUT_MS) {
      stopExponeaPolling()
      pendingExponeaCalls.length = 0
    }
  }, EXPONEA_POLL_INTERVAL_MS)
}

/**
 * Run a Bloomreach dispatch now, or as soon as the vendor snippet exists.
 *
 * Queued calls flush in call order, so an exposure enqueued before the snippet
 * loaded still precedes the events that follow it.
 */
const whenExponeaReady = (dispatch: () => void) => {
  if (isExponeaReady()) {
    // Drain anything queued while the snippet was missing first, so ordering
    // survives the gap.
    stopExponeaPolling()
    flushPendingExponeaCalls()
    dispatch()
    return
  }
  pendingExponeaCalls.push(dispatch)
  startExponeaPolling()
}

export const dispatchExponea = (name: string, data: object) => {
  if (typeof window === "undefined") return
  whenExponeaReady(() => {
    try {
      window.exponea?.track(name, data)
    } catch {
      // silent fail: tracking must never break the app
    }
  })
}

export const dispatchDataLayerPush = (
  payload: Record<string, unknown>,
  onDispatched?: () => void
): boolean => {
  try {
    if (typeof window === "undefined") return false
    window.dataLayer = window.dataLayer || []

    let finalPayload: Record<string, unknown> = payload
    // Normalize: replace legacy `ecommerce` key with standardized
    // eventModel + namespaced key. Generates event_id for deduplication.
    // Handles protected CartState code that still passes ecommerce.
    if ("ecommerce" in payload && "event" in payload) {
      const { ecommerce, ...rest } = payload
      const eventName = rest.event as string
      const normalized = {
        ...(ecommerce as Record<string, unknown>),
        event_id: uuidv7(),
      }
      finalPayload = {
        ...rest,
        eventModel: normalized,
        [`${eventName}_eventModel`]: normalized,
      }
    }

    window.dataLayer.push(finalPayload)
    mirrorToLuigi(payload) // lbx_* side-emit using pre-normalization payload. See DEV-9479.
    onDispatched?.()
    return true
  } catch {
    // silent fail: tracking must never break the app
    return false
  }
}

export const dispatchEventModelBridge = (name: string, data: object) => {
  try {
    if (typeof window === "undefined") return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      eventModel: data,
      [`${name}_eventModel`]: data,
    })
  } catch {
    // silent fail: tracking must never break the app
  }
}

export const dispatchGtagSet = (key: string, data: object) => {
  try {
    if (typeof window === "undefined") return
    window.gtag?.("set", key, data)
  } catch {
    // silent fail: tracking must never break the app
  }
}
